import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  createFilterOptions,
  Paper,
  PaperProps,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import { IClassFilterDraft } from "ts/interfaces/AdminDashboard"

import { classFilterButtonSx } from "./AdminDashboard.styles"
import useClassOptions from "./useClassOptions"

interface IClassOption {
  id: number
  name: string
}

// Pinned above the classes; picking it empties the draft. Never in `value`.
const ALL_CLASSES_ID = -1

const filterClasses = createFilterOptions<IClassOption>()

const ListFooterContext = createContext<ReactNode>(null)

// The open list's paper, with the Apply / Cancel row under the options.
const ClassListPaper = ({ children, ...props }: PaperProps) => {
  const footer = useContext(ListFooterContext)
  return (
    <Paper {...props}>
      {children}
      {footer}
    </Paper>
  )
}

interface IClassFilterProps {
  filter: IClassFilterDraft
  // Ids unknown to the loaded class list, dropped silently.
  onStaleIds: (keptIds: number[]) => void
  // The ClassFilterChips line, which names the current filter.
  summaryId: string
}

// Search field with a checkbox list. Ticks build a draft; Apply in the list (or under the field
// once it's closed) filters the table. Closing the list keeps the draft.
const ClassFilter = ({ filter, onStaleIds, summaryId }: IClassFilterProps) => {
  const { t } = useTranslation()
  const { applied, draft, setDraft, isDirty, apply, discard } = filter
  const [isOpen, setIsOpen] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const { query, classes, options } = useClassOptions(
    hasOpened || applied.length > 0,
  )
  const value = useMemo(
    () =>
      classes
        ? options.filter((option) => draft.includes(option.id))
        : draft.map((id) => ({ id, name: "" })),
    [classes, options, draft.join()],
  )

  const allClassesOption = useMemo(
    () => ({ id: ALL_CLASSES_ID, name: t("adminDashboard.allClasses") }),
    [t],
  )

  const idsKey = applied.join(",")
  useEffect(() => {
    if (!classes) return
    const kept = applied.filter((id) => classes.some((c) => c.id === id))
    if (kept.length < applied.length) onStaleIds(kept)
  }, [classes, idsKey])

  const footer = classes && options.length > 0 && (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      columnGap={1}
      sx={{
        px: 1.5,
        py: 1,
        borderTop: "1px solid",
        borderColor: "mockup.neutral90",
      }}
      // Keeps focus in the field, so the list stays open until the click lands
      onMouseDown={(event) => event.preventDefault()}
    >
      <Typography variant="body3" color="mockup.neutral30" fontWeight={500}>
        {draft.length
          ? t("adminDashboard.classesSelected", { count: draft.length })
          : t("adminDashboard.allClasses")}
      </Typography>
      <Stack direction="row" gap={0.5}>
        <Button
          variant="text"
          sx={classFilterButtonSx}
          onClick={() => {
            discard()
            setIsOpen(false)
          }}
        >
          {t("adminDashboard.cancel")}
        </Button>
        <Button
          variant="contained"
          sx={classFilterButtonSx}
          onClick={() => {
            if (isDirty) apply(draft)
            setIsOpen(false)
          }}
        >
          {t("adminDashboard.apply")}
        </Button>
      </Stack>
    </Stack>
  )

  return (
    <ListFooterContext.Provider value={footer}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        disableClearable
        open={isOpen}
        options={options}
        value={value}
        inputValue={inputValue}
        // Ticking a class keeps the search, so the next match is one click away.
        onInputChange={(_, next, reason) => {
          if (reason === "input" || reason === "clear" || reason === "blur")
            setInputValue(next)
        }}
        filterOptions={(all, state) => {
          const filtered = filterClasses(all, state)
          return state.inputValue === "" && classes && options.length
            ? [allClassesOption, ...filtered]
            : filtered
        }}
        onChange={(_, next) => {
          if (next.some((option) => option.id === ALL_CLASSES_ID)) {
            setDraft([])
            return
          }
          setDraft(next.map((option) => option.id))
        }}
        onOpen={() => {
          setIsOpen(true)
          setHasOpened(true)
          // "Try again" can't be reached with Tab, so reopening retries.
          if (query.isError && !classes) query.refetch()
        }}
        // Closing (Esc, click away, Tab) keeps the draft; Cancel drops it.
        onClose={(_, reason) => {
          if (reason !== "selectOption") setIsOpen(false)
        }}
        // The field never shows the picked classes, so Backspace mustn't drop one.
        onKeyDown={(event) => {
          if (event.key === "Backspace")
            (
              event as typeof event & { defaultMuiPrevented: boolean }
            ).defaultMuiPrevented = true
        }}
        getOptionLabel={(option) => option.name}
        // Class names can repeat, so the label can't be the key.
        getOptionKey={(option) => option.id}
        isOptionEqualToValue={(option, picked) => option.id === picked.id}
        loading={!classes && (!query.isError || query.isFetching)}
        loadingText={t("adminDashboard.loadingClasses")}
        noOptionsText={
          query.isError && !classes ? (
            <Stack alignItems="flex-start" rowGap={1}>
              {t("adminDashboard.classesError")}
              <Button
                variant="outlined"
                size="small"
                onClick={() => query.refetch()}
              >
                {t("adminDashboard.tryAgain")}
              </Button>
            </Stack>
          ) : options.length ? (
            t("adminDashboard.noMatchingClasses")
          ) : (
            t("adminDashboard.noClasses")
          )
        }
        renderOption={({ key, ...props }, option, { selected }) => {
          const isAllClasses = option.id === ALL_CLASSES_ID
          const checked = isAllClasses ? draft.length === 0 : selected
          return (
            <Box
              component="li"
              key={key}
              {...props}
              aria-selected={checked}
              sx={
                isAllClasses
                  ? {
                      borderBottom: "1px solid",
                      borderColor: "mockup.neutral90",
                    }
                  : undefined
              }
            >
              <Checkbox
                size="small"
                checked={checked}
                tabIndex={-1}
                disableRipple
                sx={{ p: 0, mr: 1 }}
              />
              {option.name}
            </Box>
          )
        }}
        renderValue={() => null}
        slots={{ paper: ClassListPaper }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={t("adminDashboard.searchClasses")}
            inputProps={{
              ...params.inputProps,
              "aria-label": t("adminDashboard.filterByClass"),
              "aria-describedby": summaryId,
            }}
          />
        )}
        sx={{
          width: { mobile: "100%", tablet: 240 },
          flexShrink: 0,
        }}
      />
    </ListFooterContext.Provider>
  )
}

export default ClassFilter
